import { useState } from "react";
import { Loader2, Star } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

const ACCOUNT_STATUS = { active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", banned: "bg-red-500/15 text-red-300 border-red-500/30" };

export default function DashProfile() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", country: user?.country || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [tm, setTm] = useState({ text: "", company: "", country: "", rating: 5 });
  const [tmLoading, setTmLoading] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault(); setSaving(true);
    try { const { data } = await api.put("/profile", form); setUser(data); toast.success("Profile updated"); }
    catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };
  const savePw = async (e) => {
    e.preventDefault(); setSavingPw(true);
    try { await api.put("/profile/password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); }
    catch (err) { toast.error(apiError(err)); }
    setSavingPw(false);
  };
  const submitTm = async (e) => {
    e.preventDefault(); setTmLoading(true);
    try { const { data } = await api.post("/testimonials/submit", { name: user.name, ...tm }); toast.success(data.message); setTm({ text: "", company: "", country: "", rating: 5 }); }
    catch (err) { toast.error(apiError(err)); }
    setTmLoading(false);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-extrabold text-white">{t("dash.profile")}</h1>
        <StatusBadge status={user?.status || "active"} map={ACCOUNT_STATUS} testid="account-status" />
      </div>

      <form onSubmit={saveProfile} className="card-lux p-6 space-y-4" data-testid="profile-form">
        <h2 className="font-semibold text-white">{t("dash.account")}</h2>
        <div><label className="label-lux">{t("auth.email")}</label><input disabled className="input-lux mt-2 opacity-60" value={user?.email} data-testid="profile-email" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="label-lux">{t("auth.name")}</label><input className="input-lux mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" /></div>
          <div><label className="label-lux">{t("auth.phone")}</label><input className="input-lux mt-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone-input" /></div>
        </div>
        <div><label className="label-lux">{t("auth.country")}</label><input className="input-lux mt-2" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="profile-country-input" /></div>
        <button disabled={saving} className="gold-btn" data-testid="profile-save-btn">{saving ? <Loader2 className="animate-spin" size={18} /> : t("common.save")}</button>
      </form>

      <form onSubmit={savePw} className="card-lux p-6 space-y-4" data-testid="password-form">
        <h2 className="font-semibold text-white">Change Password</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="label-lux">Current Password</label><input required type="password" className="input-lux mt-2" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password-input" /></div>
          <div><label className="label-lux">New Password</label><input required type="password" className="input-lux mt-2" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password-input" /></div>
        </div>
        <button disabled={savingPw} className="silver-btn" data-testid="password-save-btn">{savingPw ? <Loader2 className="animate-spin" size={18} /> : "Update Password"}</button>
      </form>

      <form onSubmit={submitTm} className="card-lux p-6 space-y-4" data-testid="testimonial-form">
        <h2 className="font-semibold text-white">Share a Testimonial</h2>
        <p className="text-xs text-slate-500">Your testimonial will be reviewed by our team before being published.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="label-lux">Company (optional)</label><input className="input-lux mt-2" value={tm.company} onChange={(e) => setTm({ ...tm, company: e.target.value })} data-testid="tm-company-input" /></div>
          <div>
            <label className="label-lux">Rating</label>
            <div className="flex gap-1 mt-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setTm({ ...tm, rating: n })} data-testid={`tm-rating-${n}`}><Star size={22} className={n <= tm.rating ? "text-amber-400 fill-amber-400" : "text-slate-600"} /></button>
              ))}
            </div>
          </div>
        </div>
        <div><label className="label-lux">Your Testimonial</label><textarea required rows={3} className="input-lux mt-2 resize-none" value={tm.text} onChange={(e) => setTm({ ...tm, text: e.target.value })} data-testid="tm-text-input" /></div>
        <button disabled={tmLoading} className="silver-btn" data-testid="tm-submit-btn">{tmLoading ? <Loader2 className="animate-spin" size={18} /> : "Submit Testimonial"}</button>
      </form>
    </div>
  );
}
